/**
 * Leaderboard Page
 * Public rankings comparing traders' performance
 * 
 * Created: 2026-01-06
 * Version: 30
 */

import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { 
    Trophy, Medal, Award, TrendingUp, TrendingDown, 
    Target, Flame, Star, Crown, Zap, ChevronDown,
    User, BarChart3, Percent, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

// API helper
const leaderboardAPI = {
    getRankings: (period, metric, limit = 50) => 
        fetch(`/api/leaderboard?period=${period}&metric=${metric}&limit=${limit}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()),
    
    getMyRank: () => 
        fetch('/api/leaderboard/me', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()),
    
    getAchievements: () => 
        fetch('/api/leaderboard/achievements', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()),
};

// ============ RANK BADGE COMPONENT ============
const RankBadge = ({ rank }) => {
    if (rank === 1) {
        return (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                <Crown className="w-5 h-5 text-white" />
            </div>
        );
    }
    if (rank === 2) {
        return (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center shadow-lg shadow-gray-400/30">
                <Medal className="w-5 h-5 text-white" />
            </div>
        );
    }
    if (rank === 3) {
        return (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Award className="w-5 h-5 text-white" />
            </div>
        );
    }
    return (
        <div className="w-10 h-10 rounded-full bg-bg-tertiary flex items-center justify-center">
            <span className="font-bold text-text-muted">{rank}</span>
        </div>
    );
};

// ============ LEADERBOARD ROW COMPONENT ============
const LeaderboardRow = ({ entry, metric, isCurrentUser }) => {
    const getValue = () => {
        switch (metric) {
            case 'total_pnl':
                return `$${(entry.totalPnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            case 'win_rate':
                return `${(entry.winRate || 0).toFixed(1)}%`;
            case 'total_trades':
                return entry.totalTrades || 0;
            case 'roi_percent':
                return `${(entry.roiPercent || 0).toFixed(1)}%`;
            default:
                return entry.totalPnl || 0;
        }
    };

    const getValueColor = () => {
        if (metric === 'total_pnl' || metric === 'roi_percent') {
            return entry.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red';
        }
        return 'text-text-primary';
    };

    return (
        <div className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
            isCurrentUser 
                ? 'bg-accent-blue/10 border border-accent-blue/30' 
                : 'bg-bg-card hover:bg-bg-tertiary'
        }`}>
            <RankBadge rank={entry.rank} />
            
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{entry.username}</span>
                    {isCurrentUser && (
                        <span className="text-xs bg-accent-blue/20 text-accent-blue px-2 py-0.5 rounded-full">You</span>
                    )}
                    {entry.rank <= 3 && (
                        <Flame className="w-4 h-4 text-accent-orange" />
                    )}
                </div>
                <div className="flex items-center gap-4 text-sm text-text-muted mt-1">
                    <span>{entry.totalTrades} trades</span>
                    <span>{(entry.winRate || 0).toFixed(0)}% win</span>
                </div>
            </div>
            
            <div className="text-right">
                <p className={`text-xl font-bold ${getValueColor()}`}>{getValue()}</p>
                <p className="text-xs text-text-muted">{
                    metric === 'total_pnl' ? 'Profit/Loss' :
                    metric === 'win_rate' ? 'Win Rate' :
                    metric === 'total_trades' ? 'Total Trades' :
                    metric === 'roi_percent' ? 'ROI' : ''
                }</p>
            </div>
        </div>
    );
};

// ============ ACHIEVEMENT CARD COMPONENT ============
const AchievementCard = ({ achievement, unlocked }) => (
    <div className={`p-4 rounded-xl border transition-all ${
        unlocked 
            ? 'bg-bg-card border-accent-green/30' 
            : 'bg-bg-tertiary/50 border-border-color opacity-50'
    }`}>
        <div className="flex items-center gap-3">
            <span className="text-3xl">{achievement.icon}</span>
            <div>
                <p className="font-semibold">{achievement.name}</p>
                <p className="text-sm text-text-muted">{achievement.description}</p>
            </div>
        </div>
        {unlocked && achievement.unlockedAt && (
            <p className="text-xs text-accent-green mt-2">
                Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
            </p>
        )}
    </div>
);

// ============ MAIN COMPONENT ============
const Leaderboard = () => {
    const [period, setPeriod] = useState('alltime');
    const [metric, setMetric] = useState('total_pnl');
    const [rankings, setRankings] = useState([]);
    const [myRank, setMyRank] = useState(null);
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('rankings');

    // Fetch data
    const fetchData = async () => {
        setLoading(true);
        try {
            const [rankingsRes, myRankRes, achievementsRes] = await Promise.all([
                leaderboardAPI.getRankings(period, metric),
                leaderboardAPI.getMyRank(),
                leaderboardAPI.getAchievements()
            ]);
            
            setRankings(rankingsRes.leaderboard || rankingsRes.rankings || []);
            setMyRank(myRankRes);
            setAchievements(achievementsRes.achievements || []);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
            toast.error('Failed to load leaderboard');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [period, metric]);

    // Period options — ids MUST match the server (daily/weekly/monthly/alltime).
    const periods = [
        { id: 'alltime', label: 'All Time' },
        { id: 'monthly', label: 'This Month' },
        { id: 'weekly', label: 'This Week' },
        { id: 'daily', label: 'Today' },
    ];

    // Metric options
    const metrics = [
        { id: 'total_pnl', label: 'Profit/Loss', icon: TrendingUp },
        { id: 'win_rate', label: 'Win Rate', icon: Percent },
        { id: 'total_trades', label: 'Most Active', icon: Activity },
    ];

    return (
        <div className="min-h-screen bg-bg-primary">
            <Navigation onRefresh={fetchData} refreshing={loading} />
            
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 mb-4">
                        <Trophy className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Leaderboard</h1>
                    <p className="text-text-muted">See how you stack up against other traders</p>
                </div>

                {/* My Stats Card */}
                {myRank && (
                    <div className="card p-6 mb-6 bg-gradient-to-r from-accent-blue/10 to-accent-purple/10 border border-accent-blue/20">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-accent-blue to-accent-purple flex items-center justify-center">
                                    <User className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold">{myRank.username}</p>
                                    <p className="text-text-muted">
                                        {Number.isFinite(myRank.percentile)
                                            ? `Top ${100 - myRank.percentile}% of traders`
                                            : 'Start trading to get ranked!'
                                        }
                                    </p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-6">
                                <div className="text-center">
                                    <p className="text-2xl font-bold">#{myRank.rankings?.byPnl?.rank || '-'}</p>
                                    <p className="text-xs text-text-muted">By P&L</p>
                                </div>
                                <div className="text-center">
                                    <p className={`text-2xl font-bold ${(myRank.stats?.totalPnl || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                        ${(myRank.stats?.totalPnl || 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-text-muted">Total P&L</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold">{myRank.stats?.winRate || 0}%</p>
                                    <p className="text-xs text-text-muted">Win Rate</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('rankings')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                            activeTab === 'rankings'
                                ? 'bg-accent-blue text-white'
                                : 'bg-bg-tertiary text-text-secondary hover:bg-bg-card'
                        }`}
                    >
                        <BarChart3 className="w-4 h-4" />
                        Rankings
                    </button>
                    <button
                        onClick={() => setActiveTab('achievements')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                            activeTab === 'achievements'
                                ? 'bg-accent-blue text-white'
                                : 'bg-bg-tertiary text-text-secondary hover:bg-bg-card'
                        }`}
                    >
                        <Star className="w-4 h-4" />
                        Achievements
                    </button>
                </div>

                {/* Rankings Tab */}
                {activeTab === 'rankings' && (
                    <>
                        {/* Filters */}
                        <div className="flex flex-wrap gap-4 mb-6">
                            {/* Period Filter */}
                            <div className="flex bg-bg-tertiary rounded-lg p-1">
                                {periods.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => setPeriod(p.id)}
                                        className={`px-4 py-2 rounded-lg text-sm transition-all ${
                                            period === p.id
                                                ? 'bg-bg-card text-text-primary'
                                                : 'text-text-muted hover:text-text-primary'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            {/* Metric Filter */}
                            <div className="flex bg-bg-tertiary rounded-lg p-1">
                                {metrics.map(m => (
                                    <button
                                        key={m.id}
                                        onClick={() => setMetric(m.id)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                                            metric === m.id
                                                ? 'bg-bg-card text-text-primary'
                                                : 'text-text-muted hover:text-text-primary'
                                        }`}
                                    >
                                        <m.icon className="w-4 h-4" />
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Top 3 Podium (Desktop) */}
                        {rankings.length >= 3 && (
                            <div className="hidden md:flex items-end justify-center gap-4 mb-8">
                                {/* 2nd Place */}
                                <div className="text-center">
                                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center mb-3 shadow-xl">
                                        <span className="text-3xl font-bold text-white">2</span>
                                    </div>
                                    <p className="font-semibold">{rankings[1]?.username}</p>
                                    <p className={`text-lg font-bold ${rankings[1]?.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                        ${(rankings[1]?.totalPnl || 0).toLocaleString()}
                                    </p>
                                    <div className="h-24 w-32 bg-gray-500/20 rounded-t-lg mt-2" />
                                </div>

                                {/* 1st Place */}
                                <div className="text-center">
                                    <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center mb-3 shadow-xl shadow-yellow-500/30">
                                        <Crown className="w-10 h-10 text-white" />
                                    </div>
                                    <p className="font-semibold text-lg">{rankings[0]?.username}</p>
                                    <p className={`text-xl font-bold ${rankings[0]?.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                        ${(rankings[0]?.totalPnl || 0).toLocaleString()}
                                    </p>
                                    <div className="h-32 w-32 bg-yellow-500/20 rounded-t-lg mt-2" />
                                </div>

                                {/* 3rd Place */}
                                <div className="text-center">
                                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mb-3 shadow-xl">
                                        <span className="text-2xl font-bold text-white">3</span>
                                    </div>
                                    <p className="font-semibold">{rankings[2]?.username}</p>
                                    <p className={`text-lg font-bold ${rankings[2]?.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                        ${(rankings[2]?.totalPnl || 0).toLocaleString()}
                                    </p>
                                    <div className="h-16 w-32 bg-orange-500/20 rounded-t-lg mt-2" />
                                </div>
                            </div>
                        )}

                        {/* Rankings List */}
                        <div className="space-y-3">
                            {loading ? (
                                <div className="text-center py-12">
                                    <div className="w-12 h-12 border-4 border-accent-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-text-muted">Loading rankings...</p>
                                </div>
                            ) : rankings.length === 0 ? (
                                <div className="text-center py-12 card">
                                    <Trophy className="w-16 h-16 text-text-muted mx-auto mb-4 opacity-50" />
                                    <p className="text-text-muted">No rankings yet for this period</p>
                                    <p className="text-sm text-text-muted mt-2">Start trading to appear on the leaderboard!</p>
                                </div>
                            ) : (
                                rankings.map(entry => (
                                    <LeaderboardRow 
                                        key={entry.userId || entry.rank}
                                        entry={entry}
                                        metric={metric}
                                        isCurrentUser={entry.isCurrentUser}
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}

                {/* Achievements Tab */}
                {activeTab === 'achievements' && (
                    <div className="space-y-6">
                        {/* Unlocked Achievements */}
                        <div>
                            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                <Star className="w-5 h-5 text-accent-yellow" />
                                Your Achievements ({achievements.filter(a => a.unlockedAt).length}/{achievements.length})
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                {achievements.filter(a => a.unlockedAt).map(achievement => (
                                    <AchievementCard key={achievement.id} achievement={achievement} unlocked={true} />
                                ))}
                                {achievements.filter(a => a.unlockedAt).length === 0 && (
                                    <p className="text-text-muted col-span-2 py-8 text-center">
                                        Start trading to unlock achievements!
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Locked Achievements */}
                        <div>
                            <h3 className="font-semibold text-lg mb-4 text-text-muted">Locked</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                {achievements.filter(a => !a.unlockedAt).map(achievement => (
                                    <AchievementCard key={achievement.id} achievement={achievement} unlocked={false} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Leaderboard;
